import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../../../api";
import urls from "../../../../../urls";
import type { Delivery } from "../../../../../utils/backend-types";

export function useDeliveryMutations(projectId: string) {
  const queryClient = useQueryClient();

  const deliveryCreateMutation = useMutation({
    mutationFn: (delivery: Delivery) => createDelivery(delivery, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deliveries", { projectId }]
      });
    }
  });

  const deliveryUpdateMutation = useMutation({
    mutationFn: (delivery: Delivery) => updateDelivery(delivery),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["deliveries", { projectId }]
      });
      queryClient.invalidateQueries({
        queryKey: ["delivery", { deliveryId: data.id }]
      });
    }
  });

  const deliveryDeletionMutation = useMutation({
    mutationFn: (deliveryId: string) => deleteDelivery(deliveryId),
    onSuccess: (_, deliveryId) => {
      queryClient.invalidateQueries({
        queryKey: ["deliveries", { projectId }]
      });
      queryClient.invalidateQueries({
        queryKey: ["delivery", { deliveryId }]
      });
    }
  });

  return {
    deliveryCreateMutation,
    deliveryUpdateMutation,
    deliveryDeletionMutation
  };
}

async function createDelivery(delivery: Delivery, projectId: string) {
  const endpointUrl = urls.api.deliveries(projectId);

  try {
    const response = await api.post<Delivery>(endpointUrl, delivery);
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
}

async function updateDelivery(delivery: Delivery) {
  const endpointUrl = urls.api.delivery(delivery.id);

  try {
    const response = await api.put<Delivery>(endpointUrl, delivery);
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
}

async function deleteDelivery(deliveryId: string) {
  const endpointUrl = urls.api.delivery(deliveryId);

  try {
    await api.delete(endpointUrl);
  } catch (error) {
    return Promise.reject(error);
  }
}
